import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/383786e0-786f-4c9b-91a3-95f9eb5f0bc2.png" 
              alt="SURTICER LTDA Logo" 
              className="h-12 w-12 object-contain"
            />
            <div className="text-lg font-light tracking-wider text-gray-900">
              SURTICER LTDA
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className="text-sm text-gray-700 hover:text-[#FF6B55] transition-colors cursor-pointer whitespace-nowrap">
              Home
            </button>
            <button onClick={() => scrollToSection('company')} className="text-sm text-gray-700 hover:text-[#FF6B55] transition-colors cursor-pointer whitespace-nowrap">
              Company Info
            </button>
            <button onClick={() => scrollToSection('products')} className="text-sm text-gray-700 hover:text-[#FF6B55] transition-colors cursor-pointer whitespace-nowrap">
              Products
            </button>
            <button onClick={() => scrollToSection('features')} className="text-sm text-gray-700 hover:text-[#FF6B55] transition-colors cursor-pointer whitespace-nowrap">
              Features
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-sm text-gray-700 hover:text-[#FF6B55] transition-colors cursor-pointer whitespace-nowrap">
              Contact
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141E30] to-[#243B55]"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="text-[#FF6B55] text-xs font-light tracking-[3px] uppercase mb-6">
            PREMIUM iOS SOLUTIONS
          </div>
          <h1 className="text-white mb-8">
            <div className="text-6xl md:text-7xl font-bold mb-2">Modern Applications</div>
            <div className="text-5xl md:text-6xl font-serif italic">for iOS Platform</div>
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto">
            Enhancing user experience and improving the security of mobile devices through innovative iOS applications designed by expert developers
          </p>
        </div>
      </section>

      {/* Company Info Section */}
      <section id="company" className="grid md:grid-cols-2 min-h-screen">
        <div className="relative bg-[#FAF8F5] overflow-hidden">
          <img 
            src="https://public.readdy.ai/ai/img_res/ddfa7310606f2dd1fefa21c70e4b80f8.jpg"
            alt="Expert Team"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="relative h-full flex flex-col justify-between p-12">
            <div className="text-gray-700 text-xs font-light tracking-wider uppercase">
              ABOUT US
            </div>
            <div className="text-white">
              <div className="text-5xl font-bold mb-2">Expert Team</div>
              <div className="text-4xl font-bold mb-2">Developing</div>
              <div className="text-6xl font-bold">Modern Apps</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white flex items-center justify-center p-12 md:p-20">
          <div className="text-center max-w-lg">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-10 leading-tight">
              SURTICER LTDA
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              A team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-6 h-6 bg-[#FF6B55] rotate-45"></div>
              <span className="text-sm text-gray-600 tracking-wider uppercase">OUR PRODUCTS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Enhancing Your Mobile Experience
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* VPN Product Card */}
            <div className="relative rounded-3xl overflow-hidden h-[520px] bg-gradient-to-br from-purple-600 to-blue-500 group cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20iPhone%20mockup%20displaying%20sleek%20VPN%20application%20interface%20with%20shield%20icon%20secure%20connection%20indicators%20and%20minimalist%20dark%20blue%20purple%20gradient%20design%20showing%20privacy%20protection%20features%20on%20mobile%20screen&width=400&height=600&seq=vpn-product-001&orientation=portrait"
                alt="VPN Product"
                className="absolute top-0 right-0 w-3/4 h-auto object-contain transform rotate-12 translate-x-8 -translate-y-4 group-hover:rotate-6 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-sm p-8">
                <h3 className="text-3xl font-bold text-white mb-2">VPN</h3>
                <p className="text-gray-200 text-base">Secure Connection</p>
              </div>
            </div>

            {/* Center Column - Two Stacked Cards */}
            <div className="flex flex-col gap-5">
              {/* Security Feature Card */}
              <div className="bg-[#FAF8F5] rounded-3xl p-8 h-[250px] shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Advanced Protection</h3>
                <p className="text-sm text-gray-600">Military-grade encryption</p>
              </div>

              {/* Performance Card */}
              <div className="relative rounded-3xl overflow-hidden h-[260px] group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20dynamic%20speed%20motion%20blur%20lines%20in%20vibrant%20electric%20blue%20and%20purple%20colors%20representing%20fast%20performance%20and%20lightning%20speed%20technology%20with%20futuristic%20digital%20aesthetic%20and%20energy%20flow%20patterns&width=400&height=300&seq=performance-bg-001&orientation=landscape"
                  alt="Performance"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="relative h-full flex flex-col justify-between p-8">
                  <div className="inline-flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-full px-4 py-2 self-start">
                    <i className="ri-flashlight-line text-yellow-400"></i>
                    <span className="text-white text-xs font-medium tracking-wider uppercase">PERFORMANCE</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white">Lightning Fast</h3>
                </div>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="relative rounded-3xl overflow-hidden h-[520px] bg-gradient-to-br from-[#FF6B55] to-pink-400 group cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20iPhone%20mockup%20showing%20elegant%20cleaner%20application%20interface%20with%20storage%20optimization%20features%20clean%20minimalist%20design%20coral%20pink%20gradient%20background%20displaying%20file%20management%20and%20device%20cleaning%20tools%20on%20mobile%20screen&width=400&height=600&seq=cleaner-product-001&orientation=portrait"
                alt="Cleaner Product"
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3/4 h-auto object-contain group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-sm p-8">
                <h3 className="text-3xl font-bold text-white mb-2">Cleaner</h3>
                <p className="text-gray-200 text-base">Optimize Storage</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-6 bg-[#FAF8F5]">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-3xl text-gray-700 leading-relaxed mb-10">
                Our team of experts develops modern applications designed to enhance user experience and improve the security of mobile devices
              </p>
              
              <div className="inline-flex items-center gap-3 bg-green-100 rounded-full px-6 py-3 cursor-pointer hover:bg-green-200 transition-colors">
                <span className="text-gray-800 font-medium">iOS PLATFORM</span>
                <i className="ri-arrow-right-line text-gray-800"></i>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Premium%20iPhone%20interface%20screenshot%20displaying%20modern%20iOS%20application%20with%20clean%20user%20interface%20design%20showing%20app%20features%20security%20elements%20and%20smooth%20user%20experience%20with%20professional%20aesthetic%20and%20contemporary%20mobile%20design%20patterns&width=600&height=700&seq=features-screenshot-001&orientation=portrait"
                  alt="App Features"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute top-8 left-8">
                  <div className="bg-white rounded-full px-6 py-3 shadow-lg">
                    <span className="text-sm font-medium text-gray-800">Available on App Store</span>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-8">
                  <p className="text-white text-lg font-medium">Trusted by Users Worldwide</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#192318] text-white py-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            {/* Company Column */}
            <div>
              <h3 className="text-[#F0EBDC] text-lg font-light mb-4">SURTICER LTDA</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Expert team developing modern applications for the iOS platform
              </p>
            </div>

            {/* Newsletter Column */}
            <div>
              <h4 className="text-gray-400 text-xs tracking-[2px] uppercase mb-4">STAY UPDATED</h4>
              <div className="relative">
                <input 
                  type="email" 
                  placeholder="Your email"
                  className="w-full bg-transparent border-b border-gray-600 pb-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#FF6B55] transition-colors"
                />
                <i className="ri-arrow-right-line absolute right-0 bottom-2 text-gray-400"></i>
              </div>
              <p className="text-gray-500 text-xs mt-2">Subscribe to our newsletter</p>
            </div>

            {/* Products Column */}
            <div>
              <h4 className="text-gray-400 text-xs tracking-[2px] uppercase mb-4">PRODUCTS</h4>
              <ul className="space-y-3">
                <li className="text-white text-base hover:text-[#FF6B55] transition-colors cursor-pointer">VPN</li>
                <li className="text-white text-base hover:text-[#FF6B55] transition-colors cursor-pointer">Cleaner</li>
                <li className="text-white text-base hover:text-[#FF6B55] transition-colors cursor-pointer">Support</li>
              </ul>
            </div>

            {/* Connect Column */}
            <div>
              <h4 className="text-gray-400 text-xs tracking-[2px] uppercase mb-4">FOLLOW US</h4>
              <ul className="space-y-3">
                <li className="text-white text-base hover:text-[#FF6B55] transition-colors cursor-pointer">Twitter</li>
                <li className="text-white text-base hover:text-[#FF6B55] transition-colors cursor-pointer">LinkedIn</li>
                <li className="text-white text-base hover:text-[#FF6B55] transition-colors cursor-pointer">Medium</li>
              </ul>
            </div>
          </div>

          {/* Contact Information Block */}
          <div className="border-t border-gray-700 pt-8 pb-8">
            <div className="flex flex-wrap gap-8 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-[#FF6B55] w-4 h-4 flex items-center justify-center"></i>
                <div>
                  <span className="text-gray-400 text-xs uppercase tracking-wider mr-2">Address</span>
                  <span className="text-white">CARRERA 14 5 A 38</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <i className="ri-building-line text-[#FF6B55] w-4 h-4 flex items-center justify-center"></i>
                <div>
                  <span className="text-gray-400 text-xs uppercase tracking-wider mr-2">City</span>
                  <span className="text-white">FLORENCIA</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <i className="ri-mail-line text-[#FF6B55] w-4 h-4 flex items-center justify-center"></i>
                <div>
                  <span className="text-gray-400 text-xs uppercase tracking-wider mr-2">Zip</span>
                  <span className="text-white">180001</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <i className="ri-global-line text-[#FF6B55] w-4 h-4 flex items-center justify-center"></i>
                <div>
                  <span className="text-gray-400 text-xs uppercase tracking-wider mr-2">Country</span>
                  <span className="text-white">Colombia</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <i className="ri-mail-send-line text-[#FF6B55] w-4 h-4 flex items-center justify-center"></i>
                <div>
                  <span className="text-gray-400 text-xs uppercase tracking-wider mr-2">Email</span>
                  <span className="text-white">help@surticerltda.com</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <i className="ri-phone-line text-[#FF6B55] w-4 h-4 flex items-center justify-center"></i>
                <div>
                  <span className="text-gray-400 text-xs uppercase tracking-wider mr-2">Number</span>
                  <span className="text-white">+1 2192584970</span>
                </div>
              </div>
            </div>
          </div>

          {/* Large Brand Typography */}
          <div className="text-center py-12">
            <h2 className="text-6xl md:text-7xl font-bold text-[#F0EBDC] tracking-tight">
              SURTICER LTDA
            </h2>
          </div>

          {/* Footer Bottom */}
          <div className="text-center pt-8 border-t border-gray-700">
            <p className="text-gray-500 text-sm">
              © 2025 SURTICER LTDA. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF6B55] transition-colors">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
